import { lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";
import Footer from "@/components/Footer";
import vbaipLogo from "@/assets/vbaip-logo.jpg";

// Lazy load below-the-fold sections to reduce initial JS
const CoursePackagesHighlight = lazy(() => import("@/components/CoursePackagesHighlight"));
const RecommendedCourses = lazy(() => import("@/components/RecommendedCourses"));
const Objectives = lazy(() => import("@/components/Objectives"));
const Industries = lazy(() => import("@/components/Industries"));
const Careers = lazy(() => import("@/components/Careers"));
const Roadmap = lazy(() => import("@/components/Roadmap"));
const CTA = lazy(() => import("@/components/CTA"));
const SocialProofNotification = lazy(() => import("@/components/SocialProofNotification"));

const SectionFallback = () => (
  <div className="py-20 flex items-center justify-center">
    <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const Index = () => {
  return (
    <main className="min-h-screen bg-background">
      <Navbar />
      <Hero />

      {/* Course Packages Highlight — top placement for visibility */}
      <Suspense fallback={<SectionFallback />}>
        <CoursePackagesHighlight />
      </Suspense>

      {/* VBAIP Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-primary/5 rounded-full blur-[150px]" />
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-col md:flex-row items-center gap-10 max-w-5xl mx-auto"
          >
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="shrink-0"
            >
              <div className="relative">
                <div className="absolute -inset-3 bg-gradient-to-r from-primary/40 via-accent/30 to-neon-blue/40 rounded-3xl blur-xl animate-pulse-neon" />
                <img
                  src={vbaipLogo}
                  alt="Viksit Bharat AI Program Logo - Government AI initiative for Indian students"
                  loading="lazy"
                  width="224"
                  height="224"
                  className="relative w-48 h-48 md:w-56 md:h-56 object-contain rounded-2xl border-2 border-primary/30 bg-card p-3 shadow-elegant"
                />
              </div>
            </motion.div>
            <div className="text-center md:text-left">
              <h2 className="text-3xl md:text-4xl font-bold mb-3">
                <span className="text-gradient">Viksit Bharat</span> AI Program
              </h2>
              <p className="text-lg text-muted-foreground mb-2">
                A national initiative to make AI & Automation education accessible to every student in India.
              </p>
              <p className="text-muted-foreground mb-6">
                Check your eligibility and get a <strong className="text-foreground">Digital Certificate</strong> to join the AI Basic Course under VBAIP — completely free assessment!
              </p>
              <Link to="/vbaip-eligibility">
                <Button size="lg" className="rounded-full px-8 shadow-elegant hover:glow-green text-lg font-semibold" aria-label="Check your eligibility for VBAIP program">
                  <Award className="mr-2 w-5 h-5" aria-hidden="true" />
                  Check Eligibility for VBAIP
                  <ArrowRight className="ml-2 w-5 h-5" aria-hidden="true" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SEO Section */}
      <section className="py-16 bg-card/30">
        <div className="container mx-auto px-6 max-w-5xl">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-6">
            Why Choose <span className="text-gradient">Digiadwords</span> — Best Digital Marketing Academy in Gwalior
          </h2>
          <p className="text-muted-foreground text-center text-lg leading-relaxed mb-4">
            Digiadwords is the best Digital Marketing Academy in Gwalior offering expert Digital Marketing Coaching and AI & Automation Coaching for students, job seekers, and business professionals. Learn AI with Sumit Sharma through our job-oriented programs covering SEO, Social Media Marketing, Google Ads, Content Marketing, Email Marketing, and AI-powered automation tools.
          </p>
          <p className="text-muted-foreground text-center leading-relaxed">
            Whether you're looking for Digital Marketing Coaching in Gwalior or AI and Automation Coaching in Gwalior, Digiadwords provides hands-on learning with live projects, internship support, and career placement assistance. Join the Viksit Bharat AI Program and become job-ready in 4–6 months.
          </p>
        </div>
      </section>

      <Suspense fallback={<SectionFallback />}>
        <RecommendedCourses />
      </Suspense>
      <Suspense fallback={<SectionFallback />}>
        <Objectives />
      </Suspense>
      <Suspense fallback={<SectionFallback />}>
        <Roadmap />
      </Suspense>
      <Suspense fallback={<SectionFallback />}>
        <Industries />
      </Suspense>
      <Suspense fallback={<SectionFallback />}>
        <Careers />
      </Suspense>
      <Suspense fallback={<SectionFallback />}>
        <CTA />
      </Suspense>
      <Footer />
      <Suspense fallback={null}>
        <SocialProofNotification />
      </Suspense>
    </main>
  );
};

export default Index;
